import { getTelegramInitData } from '../../core/api.js';
import { getIsOwnerApp } from '../../app/app-state.js';
import type { OwnerChatMessage } from '../../types/api.js';
import { getOwnerChatViewerId } from './dom.js';
import {
  appendOwnerChatMessages,
  loadOwnerChatMessages,
  upgradePendingOwnerChatMessage,
  updateOwnerChatMessageInDom,
  removeOwnerChatMessageById
} from './messages.js';
import { renderOwnerChatThreads } from './threads.js';
import {
  bumpOwnerChatLastSeenId,
  getOwnerChatPendingOutgoingId,
  setOwnerChatPendingOutgoingId
} from './session.js';

interface OwnerChatWsOutbound {
  type: string;
  viewerId?: string;
  conversationUserId?: string;
}

interface OwnerChatWsInbound {
  type: string;
  threads?: import('../../types/api.js').OwnerChatThread[];
  messages?: OwnerChatMessage[];
  message?: OwnerChatMessage;
  messageId?: string | number;
  conversationUserId?: string | number;
}

// ----- WebSocket для чата (без polling) -----
let ownerChatWs: WebSocket | null = null;
let ownerChatWsActive = false;
let ownerChatWsSubscribedThreads = false;
let ownerChatWsSubscribedConversationUserId: string | null = null;
let ownerChatWsReconnectTimer: ReturnType<typeof setTimeout> | null = null;
let ownerChatWsRetry = 0;
let ownerChatPollTimer: ReturnType<typeof setInterval> | null = null;

function ownerChatWsUrl() {
  const wsProto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return wsProto + '//' + location.host + '/ws/owner-chat?initData=' + encodeURIComponent(getTelegramInitData() || '');
}

function ownerChatWsSend(payload: OwnerChatWsOutbound) {
  if (!ownerChatWs || ownerChatWs.readyState !== WebSocket.OPEN) return;
  try { ownerChatWs.send(JSON.stringify(payload)); } catch (_) {}
}

function ownerChatWsApplySubscriptions() {
  if (ownerChatWsSubscribedThreads) {
    ownerChatWsSend({ type: 'subscribeThreads', viewerId: getOwnerChatViewerId() });
  }
  if (ownerChatWsSubscribedConversationUserId) {
    ownerChatWsSend({
      type: 'subscribeMessages',
      viewerId: getOwnerChatViewerId(),
      conversationUserId: ownerChatWsSubscribedConversationUserId
    });
  }
}

function ownerChatWsEnsure() {
  if (!ownerChatWsActive) return;
  if (ownerChatWs && ownerChatWs.readyState === WebSocket.OPEN) return;

  if (ownerChatWsReconnectTimer) clearTimeout(ownerChatWsReconnectTimer);
  ownerChatWsReconnectTimer = null;

  try {
    ownerChatWs = new WebSocket(ownerChatWsUrl());
  } catch (_) {
    ownerChatWs = null;
    return;
  }

  ownerChatWs.onopen = function () {
    ownerChatWsRetry = 0;
    ownerChatWsApplySubscriptions();
    // Сразу подтянуть пропущенные, если WS только что подключился.
    loadOwnerChatMessages({ reset: false, fromPoll: true }).catch(() => {});
  };

  ownerChatWs.onmessage = function (ev: MessageEvent<string>) {
    let data: OwnerChatWsInbound | null = null;
    try { data = JSON.parse(ev.data) as OwnerChatWsInbound; } catch (_) { data = null; }
    if (!data || !data.type) return;

    if (data.type === 'threads') {
      if (!getIsOwnerApp()) return;
      if (!ownerChatWsSubscribedThreads) return;
      if (Array.isArray(data.threads)) renderOwnerChatThreads(data.threads);
      return;
    }

    if (data.type === 'messages') {
      if (!ownerChatWsSubscribedConversationUserId) return;
      const convId = data.conversationUserId != null ? String(data.conversationUserId) : '';
      if (String(convId) !== String(ownerChatWsSubscribedConversationUserId)) return;
      if (!Array.isArray(data.messages) || data.messages.length === 0) return;

      const meId = getOwnerChatViewerId();
      const incomingMsgs: OwnerChatMessage[] = [];
      const ownMsgs: OwnerChatMessage[] = [];
      data.messages.forEach(function (m) {
        if (meId && String(m.fromUserId) === meId) ownMsgs.push(m);
        else incomingMsgs.push(m);
      });
      if (incomingMsgs.length) {
        const lastIn = incomingMsgs[incomingMsgs.length - 1];
        if (lastIn && lastIn.id != null) {
          bumpOwnerChatLastSeenId(Number(lastIn.id));
        }
        appendOwnerChatMessages(incomingMsgs, {
          animate: true,
          instantScroll: true,
          incoming: true,
          deferScroll: true
        });
      }
      if (ownMsgs.length) {
        const lastOwn = ownMsgs[ownMsgs.length - 1];
        if (lastOwn && lastOwn.id != null) {
          bumpOwnerChatLastSeenId(Number(lastOwn.id));
        }
        const pendingOutgoingId = getOwnerChatPendingOutgoingId();
        if (pendingOutgoingId != null && lastOwn) {
          if (upgradePendingOwnerChatMessage(pendingOutgoingId, lastOwn)) {
            setOwnerChatPendingOutgoingId(null);
            return;
          }
        }
        // Эхо своих сообщений по WS — без анимации (уже показали при отправке).
        appendOwnerChatMessages(ownMsgs, { animate: false, instantScroll: true });
      }
      return;
    }

    if (data.type === 'messageUpdated') {
      if (!ownerChatWsSubscribedConversationUserId) return;
      const convId = data.conversationUserId != null ? String(data.conversationUserId) : '';
      if (String(convId) !== String(ownerChatWsSubscribedConversationUserId)) return;
      if (!data.message) return;
      updateOwnerChatMessageInDom(data.message);
      return;
    }

    if (data.type === 'messageDeleted') {
      if (!ownerChatWsSubscribedConversationUserId) return;
      const convId = data.conversationUserId != null ? String(data.conversationUserId) : '';
      if (String(convId) !== String(ownerChatWsSubscribedConversationUserId)) return;
      if (data.messageId == null) return;
      removeOwnerChatMessageById(data.messageId);
    }
  };

  ownerChatWs.onclose = function () {
    ownerChatWs = null;
    if (!ownerChatWsActive) return;
    ownerChatWsRetry++;
    var delay = Math.min(2000, 150 + 150 * ownerChatWsRetry);
    ownerChatWsReconnectTimer = setTimeout(ownerChatWsEnsure, delay);
  };

  ownerChatWs.onerror = function () {
    try { ownerChatWs?.close(); } catch (_) {}
  };
}

function ownerChatPollTick() {
  if (ownerChatWs && ownerChatWs.readyState === WebSocket.OPEN) return;
  loadOwnerChatMessages({ reset: false, fromPoll: true }).catch(() => {});
}

document.addEventListener('visibilitychange', function () {
  if (document.visibilityState !== 'visible') return;
  if (!ownerChatWsActive) return;
  if (ownerChatWs && ownerChatWs.readyState === WebSocket.OPEN) return;
  ownerChatWsEnsure();
});

export function stopOwnerChatPolling() {
  if (ownerChatPollTimer) {
    clearInterval(ownerChatPollTimer);
    ownerChatPollTimer = null;
  }

  ownerChatWsActive = false;
  ownerChatWsSubscribedThreads = false;
  ownerChatWsSubscribedConversationUserId = null;

  if (ownerChatWsReconnectTimer) clearTimeout(ownerChatWsReconnectTimer);
  ownerChatWsReconnectTimer = null;

  if (ownerChatWs) {
    try { ownerChatWs.close(); } catch (_) {}
    ownerChatWs = null;
  }
}


export function subscribeOwnerChatThreads() {
  ownerChatWsActive = true;
  ownerChatWsSubscribedThreads = true;
  ownerChatWsSubscribedConversationUserId = null;
  ownerChatWsEnsure();
}

export function subscribeOwnerChatConversation(conversationUserId: string | number | null | undefined) {
  ownerChatWsActive = true;
  ownerChatWsSubscribedThreads = false;
  ownerChatWsSubscribedConversationUserId = conversationUserId ? String(conversationUserId) : null;
  ownerChatWsEnsure();
}

export function startOwnerChatPollFallback() {
  if (!ownerChatPollTimer) ownerChatPollTimer = setInterval(ownerChatPollTick, 500);
}
